import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, fmtDateTime, useServerEvents } from '../lib/util';

const ORIGIN: Record<string, string> = { ai: 'AI', user: 'You', undo: 'Undo', resolution: 'Resolution', import: 'Import', restore: 'Restore' };
const TABLE: Record<string, string> = {
  entities: 'record',
  entity_aliases: 'alias',
  claims: 'claim',
  claim_entities: 'claim link',
  note_items: 'organized note',
  note_item_claims: 'note link',
  sources: 'source link',
  entity_mentions: 'mention',
  clarifications: 'question',
  clarification_links: 'question link',
  entries: 'note rewrite',
};

export function History({ campaignId }: { campaignId: string }) {
  const [list, setList] = useState<any[] | null>(null);
  const [err, setErr] = useState('');
  const load = async () => {
    try {
      setList(await api(`/api/campaigns/${campaignId}/history`));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
  }, [campaignId]);
  useServerEvents(campaignId, (w) => w === 'records' && void load());
  return (
    <div className="page narrow">
      <h1>History</h1>
      <p className="muted">Every automatic and manual change to records, with its sources. Undo checks for newer changes that depend on it first, so later edits are never destroyed.</p>
      {err && <p className="error">{err}</p>}
      {list === null ? (
        <p className="muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted">No changes yet.</p>
      ) : (
        <ul className="history">
          {list.map((cs) => (
            <ChangeSet key={cs.id} cs={cs} campaignId={campaignId} onChanged={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ChangeSet({ cs, campaignId, onChanged }: { cs: any; campaignId: string; onChanged: () => void }) {
  const [detail, setDetail] = useState<any>(null);
  const [blockers, setBlockers] = useState<any[] | null>(null);
  const [err, setErr] = useState('');
  const toggle = async () => {
    if (detail) return setDetail(null);
    try {
      setDetail(await api(`/api/campaigns/${campaignId}/history/${cs.id}`));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const undo = async () => {
    setErr('');
    try {
      const chk = await api(`/api/campaigns/${campaignId}/history/${cs.id}/undo-check`);
      if (chk.blockers.length) return setBlockers(chk.blockers);
      if (!confirm(`Undo “${cs.summary}”?`)) return;
      await api(`/api/campaigns/${campaignId}/history/${cs.id}/undo`, { body: {} });
      setBlockers(null);
      onChanged();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  return (
    <li className={classNames('cs', cs.undone_by_id && 'undone')}>
      <div className="cs-head">
        <span className={classNames('tag', `origin-${cs.origin}`)}>{ORIGIN[cs.origin] ?? cs.origin}</span>
        <span className="grow">{cs.summary}</span>
        <span className="muted small">{fmtDateTime(cs.created_at)}</span>
      </div>
      <div className="row">
        <button className="link small" onClick={() => void toggle()} aria-expanded={!!detail}>
          {detail ? 'Hide' : `Show ${cs.change_count} change${cs.change_count === 1 ? '' : 's'}`}
        </button>
        {cs.undone_by_id ? <span className="muted small">undone</span> : cs.change_count > 0 && cs.origin !== 'restore' ? <button className="link small" onClick={() => void undo()}>Undo</button> : null}
      </div>
      {blockers && (
        <div className="notice">
          <p>Newer changes depend on this one. Undo them first (newest first):</p>
          <ul>
            {blockers.map((b) => (
              <li key={b.changeSetId || b.summary}>
                {b.summary} <span className="muted small">— {b.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="error small">{err}</p>}
      {detail && (
        <div className="cs-detail">
          {detail.sourceEntries?.length > 0 && (
            <>
              <h4>Based on these notes</h4>
              <ul className="sources">
                {detail.sourceEntries.map((e: any) => (
                  <li key={e.id}>{e.text}</li>
                ))}
              </ul>
            </>
          )}
          <h4>Changes</h4>
          <ul className="changes">
            {detail.changes.map((c: any) => (
              <li key={c.seq}>
                <span className="tag subtle">
                  {c.op} {TABLE[c.table] ?? c.table}
                </span>{' '}
                {describe(c)}
                {c.note && <span className="muted small"> — {c.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function describe(c: any): string {
  const row = c.after ?? c.before ?? {};
  const label = row.statement ?? row.name ?? row.text ?? row.alias ?? row.question ?? row.rewritten ?? '';
  if (c.op === 'update' && c.before && c.after) {
    const changed = Object.keys(c.after).filter((k) => k !== 'updated_at' && c.before[k] !== c.after[k]);
    return `${label ? `“${String(label).slice(0, 80)}”: ` : ''}${changed.map((k) => `${k} ${fmt(c.before[k])} → ${fmt(c.after[k])}`).join(', ')}`;
  }
  return label ? `“${String(label).slice(0, 120)}”` : '';
}
const fmt = (v: unknown) => (v === null || v === undefined ? '∅' : String(v).length > 60 ? `${String(v).slice(0, 60)}…` : String(v));
